import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';
import * as Sentry from '@sentry/react';
import { initI18n } from '@/lib/i18n';
import { sharedSentryPolicy } from '@/lib/observability';
import './styles/globals.css';
import { router } from './router';
import { createQueryClient } from './lib/queryClient';
import { AuthProvider } from './providers/AuthProvider';
import { tokenStore } from '@/lib/http-core';

// Slice 6 ops hardening: Sentry SPA init.
// DSN 미주입 시 init 자체를 skip → SDK 가 silent no-op. 빌드/dev 환경 부담 없음.
// PII 정책 (replay text mask + networkDetailDenyUrls /pickups/* /guest-orders/*) 는
// packages/observability 의 sharedSentryPolicy 단일 정본 재사용 (RFC-001 §4.6).
const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) ?? import.meta.env.MODE,
    release: (import.meta.env.VITE_GIT_SHA as string) ?? 'unknown',
    tracesSampleRate: sharedSentryPolicy.tracesSampleRate,
    replaysSessionSampleRate: sharedSentryPolicy.replaysSessionSampleRate,
    replaysOnErrorSampleRate: sharedSentryPolicy.replaysOnErrorSampleRate,
    integrations: [
      Sentry.replayIntegration({
        ...sharedSentryPolicy.replayIntegrationOptions,
        networkDetailDenyUrls: [...sharedSentryPolicy.replayIntegrationOptions.networkDetailDenyUrls],
        networkRequestHeaders: [...sharedSentryPolicy.replayIntegrationOptions.networkRequestHeaders],
        networkResponseHeaders: [...sharedSentryPolicy.replayIntegrationOptions.networkResponseHeaders],
      }),
      Sentry.browserTracingIntegration(),
    ],
    beforeSend: sharedSentryPolicy.beforeSend,
    sendDefaultPii: false,
  });
}

const queryClient = createQueryClient();

const bootstrap = async () => {
  await initI18n({
    loadPath: '/locales',
    fallbackLng: 'ko-KR',
    ns: [
      'common',
      'errors',
      'status',
      'products',
      'auth',
      'orders',
      'payments',
      'claims',
      'vendors',
      'artists',
      'funding',
      'settlement',
      'inventory',
      'shipping',
      'manualOrder',
      'shippingFee',
      'overseasShipping',
      'notifications',
    ],
  });

  const root = document.getElementById('root');
  if (!root) throw new Error('#root not found');

  if (import.meta.env.VITE_E2E === 'true') {
    (window as Window & { __everline_tokenStore__?: typeof tokenStore }).__everline_tokenStore__ = tokenStore;
  }

  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
};

void bootstrap();
